import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import { useCallback, useEffect } from 'react';
import createSyarahV2Fetcher from "@/common/fetchSyarahv2";
import useLocalStorage from "@hooks/useLocalStorage";
import { RSAEncryption } from "@/utils/rsaEncryption";
import { useSession } from "@/_MiniApps/auth/AuthContext";
var LOCAL_STORAGE_KEY = 'event_tracking_v3';
var INTERVAL_IN_MS = 2000;
// Shared mutex map for coordination between hook and standalone functions
var mutexMap = {};
var _useLocalStorage = useLocalStorage(false),
  getLocalStorage = _useLocalStorage.getLocalStorage,
  setLocalStorage = _useLocalStorage.setLocalStorage;
// Freshwork configuration from environment variables
var FRESHWORK_CONFIG = {
  WORKER_URL: process.env.RAZZLE_FRESHWORK_WORKER_URL || '',
  ENCRYPTION_ENABLED: process.env.RAZZLE_FRESHWORK_ENCRYPTION_ENABLED === 'true',
  PUBLIC_KEY: process.env.RAZZLE_FRESHWORK_PUBLIC_KEY || ''
};

// Standalone function implementation
function acquireMutex(_x) {
  return _acquireMutex.apply(this, arguments);
}
function _acquireMutex() {
  _acquireMutex = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3(key) {
    var releaseFunc, waitForPrev, newPromise;
    return _regenerator().w(function (_context3) {
      while (1) switch (_context3.n) {
        case 0:
          waitForPrev = mutexMap[key] || Promise.resolve();
          newPromise = new Promise(function (resolve) {
            releaseFunc = resolve;
          });
          mutexMap[key] = newPromise;
          _context3.n = 1;
          return waitForPrev;
        case 1:
          return _context3.a(2, function () {
            releaseFunc();
            delete mutexMap[key];
          });
      }
    }, _callee3);
  }));
  return _acquireMutex.apply(this, arguments);
}
export function pushEventToQueue(_x2) {
  return _pushEventToQueue.apply(this, arguments);
}

// React Hook implementation with encryption support
function _pushEventToQueue() {
  _pushEventToQueue = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4(event) {
    var release, eventsStr, events;
    return _regenerator().w(function (_context4) {
      while (1) switch (_context4.n) {
        case 0:
          _context4.n = 1;
          return acquireMutex(LOCAL_STORAGE_KEY);
        case 1:
          release = _context4.v;
          try {
            eventsStr = getLocalStorage(LOCAL_STORAGE_KEY);
            events = eventsStr ? JSON.parse(eventsStr) : [];
            events.push(event);
            setLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify(events));
          } finally {
            release();
          }
        case 2:
          return _context4.a(2);
      }
    }, _callee4);
  }));
  return _pushEventToQueue.apply(this, arguments);
}
export function useLocalStorageEventConsumer() {
  var _useSession = useSession(),
    user = _useSession.user;
  var processEvents = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
    var release, eventsStr, events, phoneNumber, rsaEncryption, results, _t;
    return _regenerator().w(function (_context2) {
      while (1) switch (_context2.p = _context2.n) {
        case 0:
          _context2.n = 1;
          return acquireMutex(LOCAL_STORAGE_KEY);
        case 1:
          release = _context2.v;
          _context2.p = 2;
          eventsStr = getLocalStorage(LOCAL_STORAGE_KEY);
          events = eventsStr ? JSON.parse(eventsStr) : [];
          if (!(events.length === 0)) {
            _context2.n = 3;
            break;
          }
          return _context2.a(2);
        case 3:
          phoneNumber = user === null || user === void 0 ? void 0 : user.phone_number; // Initialize RSA encryption if enabled
          rsaEncryption = null;
          if (!(FRESHWORK_CONFIG.ENCRYPTION_ENABLED && FRESHWORK_CONFIG.PUBLIC_KEY)) {
            _context2.n = 4;
            break;
          }
          rsaEncryption = new RSAEncryption(FRESHWORK_CONFIG.PUBLIC_KEY);
          _context2.n = 4;
          return rsaEncryption.initialize();
        case 4:
          _context2.n = 5;
          return Promise.allSettled(events.map(/*#__PURE__*/function () {
            var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(eventItem) {
              var payload, encryptedPhone, workerUrl, response;
              return _regenerator().w(function (_context) {
                while (1) switch (_context.n) {
                  case 0:
                    payload = _objectSpread(_objectSpread({}, eventItem.params), {}, {
                      event_name: eventItem.eventName,
                      timestamp: new Date().toISOString()
                    }); // Add encrypted phone if encryption is enabled
                    if (!FRESHWORK_CONFIG.ENCRYPTION_ENABLED) {
                      _context.n = 4;
                      break;
                    }
                    if (!(phoneNumber && rsaEncryption)) {
                      _context.n = 2;
                      break;
                    }
                    _context.n = 1;
                    return rsaEncryption.encrypt(phoneNumber);
                  case 1:
                    encryptedPhone = _context.v;
                    if (encryptedPhone) {
                      payload.encrypted_phone = encryptedPhone;
                    } else {
                      payload.phone_number = phoneNumber;
                    }
                    _context.n = 3;
                    break;
                  case 2:
                    if (phoneNumber) {
                      payload.phone_number = phoneNumber;
                    }
                  case 3:
                    _context.n = 5;
                    break;
                  case 4:
                    if (phoneNumber) {
                      payload.phone_number = phoneNumber;
                    }
                  case 5:
                    if (!FRESHWORK_CONFIG.WORKER_URL) {
                      _context.n = 8;
                      break;
                    }
                    // Send directly to Cloudflare Worker
                    workerUrl = FRESHWORK_CONFIG.WORKER_URL;
                    console.log('[Freshwork] Sending to worker:', workerUrl, payload.event_name);
                    _context.n = 6;
                    return fetch(workerUrl, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                      },
                      body: JSON.stringify(payload)
                    });
                  case 6:
                    response = _context.v;
                    _context.n = 7;
                    return response.json();
                  case 7:
                    return _context.a(2, _context.v);
                  case 8:
                    return _context.a(2, createSyarahV2Fetcher({
                      throwValidationException: false
                    }).post("/log/freshwork", eventItem.params));
                  case 9:
                    return _context.a(2);
                }
              }, _callee);
            }));
            return function (_x3) {
              return _ref2.apply(this, arguments);
            };
          }()));
        case 5:
          results = _context2.v;
          setLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify([]));
          _context2.n = 7;
          break;
        case 6:
          _context2.p = 6;
          _t = _context2.v;
          console.error('Error processing events:', _t);
        case 7:
          _context2.p = 7;
          release();
          return _context2.f(7);
        case 8:
          return _context2.a(2);
      }
    }, _callee2, null, [[2, 6, 7, 8]]);
  })), [LOCAL_STORAGE_KEY]);
  useEffect(function () {
    var intervalId = setInterval(processEvents, INTERVAL_IN_MS);
    return function () {
      return clearInterval(intervalId);
    };
  }, [processEvents]);
}